<template>
    <div>
        <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-row class="px-4 pt-2">
            <v-col md="4" class="py-0">
                <v-autocomplete
                    v-model="merchant"
                    label="Select a merchant"
                    :items="merchants"
                    item-value="uuid"
                    item-text="name"
                    dense
                    clearable
                    @input="filterDashboardStats(); getAuthBreachLevels(); getDashboardUserStats();"
                ></v-autocomplete>
            </v-col>
        </v-row>

        <v-row v-if="chartData" class="px-4">
            <v-col md="4" class="pa-2">
                <chart1
                    ref="chart1"
                    :chartData="chartData"
                ></chart1>
            </v-col>
             <v-col md="4" class="pa-2">
                <chart2
                    ref="chart2"
                    :chartData="chartData"
                ></chart2>
            </v-col>
            <v-col md="4" class="pa-2">
                <chart3
                    ref="chart3"
                    :chartData="chartData"
                ></chart3>
            </v-col>
        </v-row>

        <v-row v-if="topMerchantsRevenue && topDivisionsRevenue && chartData && uri == 'live'" class="px-4">
            <v-col md="4" class="pa-2">
                <v-card class="pa-2" color="#42A5F5">
                    <v-card-subtitle class="text-center white--text py-2">Top 10 Merchants - Last 12 Weeks Revenue</v-card-subtitle>
                    <v-data-table
                        :headers="headers1"
                        :items="topMerchantsRevenue"
                        class="table-blue"
                        hide-default-footer
                        dense
                    >
                    <template v-slot:item.totalRevenue="{ item }">
                        {{ Intl.NumberFormat('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(item.totalRevenue) }}
                    </template>
                    <template v-slot:item.numTransactions="{ item }">
                        {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(item.numTransactions ) }}
                    </template>
                    </v-data-table>
                </v-card>
            </v-col>

            <v-col md="4" class="pa-2">
                <v-card class="pa-2" color="#42A5F5">
                    <v-card-subtitle class="text-center white--text py-2">Top 10 Divisions - Last 12 Weeks Revenue</v-card-subtitle>
                    <v-data-table
                        :headers="headers2"
                        :items="topDivisionsRevenue"
                        class="table-blue"
                        hide-default-footer
                        dense
                    >
                    <template v-slot:item.totalRevenue="{ item }">
                        {{ Intl.NumberFormat('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(item.totalRevenue )}}
                    </template>
                    <template v-slot:item.numTransactions="{ item }">
                        {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(item.numTransactions ) }}
                    </template>
                    </v-data-table>
                </v-card>
            </v-col>

            <v-col md="4" class="px-3 pt-0">
                <v-row>
                    <v-col v-if="authLevels" md="12" class="pa-2">
                        <v-card class="pa-2" color="#42A5F5">
                            <v-card-subtitle class="text-center white--text py-2"><b>Authorisation Statistics For {{ authLevels[0].monthName }}</b></v-card-subtitle>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Total Transactions:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].totalTransactions) }} / 20,000</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Visa Transactions:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].visaTotal) }}</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Mastercard Transactions:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].masterTotal) }}</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col v-if="authLevels" md="6" class="pa-2">
                        <v-card class="pa-2" color="#42A5F5" :class="breach">
                            <v-card-subtitle class="text-center white--text py-2"><b>Visa Statistics</b></v-card-subtitle>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Successful:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].visaSuccess) }} / {{ authLevels[0].visaSuccessRatio }}%</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Failure:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].visaFail) }} / {{ authLevels[0].visaFailureRatio }}%</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Breach Threshold:</b> {{ authLevels[0].visaBreach }}%</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col v-if="authLevels" md="6" class="pa-2">
                        <v-card class="pa-2" color="#42A5F5" :class="breach">
                            <v-card-subtitle class="text-center white--text py-2"><b>Mastercard Statistics</b></v-card-subtitle>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Successful:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].masterSuccess) }} / {{ authLevels[0].masterSuccessRatio }}%</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Failure:</b> {{ Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(authLevels[0].masterFail) }} / {{ authLevels[0].masterFailureRatio }}%</v-card-text>
                            <v-card-text class="white--text px-2 py-0 caption"><b>Breach Threshold:</b> {{ authLevels[0].masterBreach }}%</v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>

            <v-col md="4" class="pa-2">
                <v-card class="pa-2" color="#E57373">
                    <v-card-subtitle class="text-center white--text py-2">Chargebacks for {{ monthName }}</v-card-subtitle>
                    <v-data-table
                        :headers="headers3"
                        :items="topChargebacksMerchants"
                        class="table-red"
                        hide-default-footer
                        dense
                    >
                        <template v-slot:item.cbkRatio="{ item }">
                            {{ `${item.cbkRatio}%` }}
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>

            <v-col v-if="userStats && userStats.length > 0" md="4" class="pa-2">
                <v-card class="pa-2" color="#42A5F5">
                    <v-card-subtitle class="text-center white--text py-2">User Statistics for {{ monthName }}</v-card-subtitle>
                    <v-data-table
                        :headers="headers4"
                        :items="userStats"
                        class="table-blue"
                        hide-default-footer
                        dense
                    >
                        <template v-slot:item.val="{ item }">
                            {{ Intl.NumberFormat('en-US', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(item.val )}}
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Revenue from '@/components/dashboards/charts/Revenue.vue';
import Refund from '@/components/dashboards/charts/Refund.vue';
import RevenueFailed from '@/components/dashboards/charts/RevenueFailed.vue';

export default {
    components: {
        'chart1': Revenue,
        'chart2': Refund,
        'chart3': RevenueFailed
    },
    data() {
        return {
            chartData: null,
            merchants: null,
            merchant: null,
            loading: true,
            topMerchantsRevenue: [],
            topDivisionsRevenue: [],
            topChargebacksMerchants: [],
            topChargebacksAcquirers: [],
            authLevels: null,
            userStats: null,
            monthName: moment().format('MMMM'),
            headers1: [{ text: 'Merchant', value: 'merchantName', sortable: false, align: 'left' },
                       { text: 'Revenue', value: 'totalRevenue', sortable: false, align: 'left' },
                       { text: 'Transactions', value: 'numTransactions', sortable: false, align: 'left' }],
            headers2: [{ text: 'Division', value: 'divisionName', sortable: false, align: 'left' },
                       { text: 'Revenue', value: 'totalRevenue', sortable: false, align: 'left' },
                       { text: 'Transactions', value: 'numTransactions', sortable: false, align: 'left' }],
            headers3: [{ text: 'Merchant', value: 'merchantName', sortable: false, align: 'left' },
                       { text: 'MID', value: 'merchantId', sortable: false, align: 'left' },
                       { text: 'Scheme', value: 'scheme', sortable: false, align: 'center' },
                       { text: 'Chargebacks', value: 'numOfCbks', sortable: false, align: 'center' },
                       { text: 'Ratio', value: 'cbkRatio', sortable: false, align: 'left' }],
            headers4: [{ text: 'Agent', value: 'agent', sortable: false, align: 'left' },
                       { text: 'Cards', value: 'cards', sortable: false, align: 'left' },
                       { text: 'Payments', value: 'vol', sortable: false, align: 'left' },
                       { text: 'Revenue', value: 'val', sortable: false, align: 'left' }]
        }
    },
    watch: {
        uri() {
            this.merchant = null;
            this.authLevels = null;
            this.getMerchants();
            this.getDashboardStats();
        }
    },
    computed: {
        uri() {
            return this.$store.state.sysState ? 'live' : 'test';
        },
        breach: function() {
            return this.authLevels[0].visaBreach <= 50.00 ? 'breach-green'
                   : this.authLevels[0].visaBreach > 50.00 && this.authLevels[0].visaBreach <= 75.00 ? 'breach-orange'
                   : 'breach-red';
        }
    },
    beforeMount() {
        this.getMerchants();
        this.getDashboardStats();
        this.getTopMerchantsRevenue();
        this.getTopDivisionsRevenue();
        this.getTopChargebacksMerchants();
        this.getAuthBreachLevels();
        this.getDashboardUserStats();
    },
    methods: {
        getDashboardStats() {
            axios({
            method: 'get',
            url: `get-dashboard-stats-${this.uri}`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.loading = false;
                this.chartData = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        filterDashboardStats() {
            this.loading = true;

            if(this.merchant) {
                axios({
                method: 'post',
                url: `filter-dashboard-stats-${this.uri}`,
                data: { merchant: this.merchant },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.loading = false;
                    this.chartData = response.data;
                })
                .catch(e => {
                    this.errors.push(e)
                })
            }
            else {
                this.getDashboardStats();
            }
        },
        getMerchants() {
            axios({
            method: 'get',
            url: `get-merchants-${this.uri}`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.merchants = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getTopMerchantsRevenue() {
            axios({
            method: 'get',
            url: `get-top-merchants-revenue-${this.uri}`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.topMerchantsRevenue = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getTopDivisionsRevenue() {
            axios({
            method: 'get',
            url: `get-top-divisions-revenue-${this.uri}`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.topDivisionsRevenue = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getAuthBreachLevels() {
            if(this.merchant && this.uri == 'live') {
                axios({
                method: 'post',
                url: `get-auth-breach-levels-${this.uri}`,
                data: { merchant: this.merchant },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.authLevels = response.data.length ? response.data : null;
                })
                .catch(e => {
                    this.errors.push(e)
                })
            }
            else {
                this.authLevels = null;
            }
        },
        getTopChargebacksMerchants() {
            axios({
            method: 'get',
            url: `get-top-chargebacks-merchants-${this.uri}`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.topChargebacksMerchants = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getDashboardUserStats() {
            if(this.merchant && this.uri == 'live') {
                axios({
                method: 'post',
                url: `get-dashboard-user-stats-${this.uri}`,
                data: { merchant: this.merchant },
                config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
                })
                .then(response => {
                    this.userStats = response.data;
                    console.log(this.userStats);
                })
                .catch(e => {
                    this.errors.push(e)
                })
            }
            else {
                this.userStats = null;
            }
        }
    }
}
</script>

<style>
    .table-blue {
        background: #42A5F5 !important;
        color: #fff !important;
    }

    .table-blue .v-data-table-header tr th span,
    .table-red .v-data-table-header tr th span {
        color: #fff;
    }

    .table-blue tbody tr:hover {
        background: #64B5F6 !important;
    }

    .table-blue thead tr:last-child th,
    .table-red thead tr:last-child th,
    .table-blue tbody tr:not(:last-child) td:not(.v-data-table__mobile-row),
    .table-red tbody tr:not(:last-child) td:not(.v-data-table__mobile-row) {
        border-bottom: thin solid rgba(255, 255, 255, 0.2) !important;
    }

    .table-red {
        background: #E57373 !important;
        color: #fff !important;
    }

    .table-red tbody tr:hover {
        background: #EF9A9A !important;
    }

    .breach-green {
        background: #66BB6A !important;
        color: #fff !important;
    }

    .breach-red {
        background: #FB8C00 !important;
        color: #fff !important;
    }

    .breach-red {
        background: #E57373 !important;
        color: #fff !important;
    }
</style>